import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DELTA_COLORS } from '../ui/charts/chartTheme';

/**
 * KPIRow — 4 stat cards showing key metrics with period deltas.
 * REQ-DSH-01, REQ-DSH-02
 */
export default function KPIRow({ kpis = [], loading = false }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={skeletonCardStyle}>
            <div style={{ ...shimmerStyle, width: '40%', height: 14 }} />
            <div style={{ ...shimmerStyle, width: '60%', height: 28, marginTop: 8 }} />
            <div style={{ ...shimmerStyle, width: '30%', height: 12, marginTop: 8 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
      {kpis.map((kpi, index) => {
        // PR-41 (2026-08-18, §3q): the delta paragraph is rendered ONLY when
        // `kpi.delta` is nullish (null or undefined). This is PERMANENT: the
        // stored-monthly-snapshot endpoint (IDI-68, Tyler) hasn't landed, so
        // computeKPIs deliberately emits tiles without a `delta` field; and
        // even after IDI-68 lands, every new tenant lives in the delta-absent
        // state for a month regardless. Rendering nothing where the delta
        // used to sit is the right behaviour on both sides of that event.
        //
        // Nullish, not falsy: delta === 0 is a REAL value ("unchanged month
        // over month") and must render its own dash.
        const hasDelta = kpi.delta != null;
        const deltaColor = !hasDelta ? null
          : kpi.delta === 0
            ? DELTA_COLORS.neutral
            : (kpi.invertDelta ? kpi.delta < 0 : kpi.delta > 0)
              ? DELTA_COLORS.positive
              : DELTA_COLORS.negative;
        const deltaArrow = !hasDelta ? null
          : kpi.delta > 0 ? '▲'
          : kpi.delta < 0 ? '▼'
          : '–';
        const isFeatured = kpi.featured;

        return (
          <div
            key={kpi.key}
            onClick={() => {
              // Iter-18: prefer explicit href (per-KPI routing); fall back to
              // reports overview with reportId as a query param (no /reports/:id route exists).
              if (kpi.href) return navigate(kpi.href);
              if (kpi.reportId) return navigate(`/reports?report=${encodeURIComponent(kpi.reportId)}`);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              if (kpi.href) navigate(kpi.href);
              else if (kpi.reportId) navigate(`/reports?report=${encodeURIComponent(kpi.reportId)}`);
            }}
            style={{
              ...cardStyle,
              cursor: (kpi.href || kpi.reportId) ? 'pointer' : 'default',
              ...(isFeatured ? { borderTop: '3px solid var(--color-action-primary)' } : {}),
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-action-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--neutral-200)'; }}
          >
            <p style={labelStyle}>{kpi.label}</p>
            <p style={{ ...valueStyle, ...(isFeatured ? { color: 'var(--color-action-primary)' } : {}) }}>{kpi.value}</p>
            {hasDelta && (
              <p style={{ ...deltaStyle, color: deltaColor }}>
                {deltaArrow} {Math.abs(kpi.delta)}% vs last month
              </p>
            )}
            {kpi.sub && <p style={subStyle}>{kpi.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

const cardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '20px 24px',
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const skeletonCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '20px 24px',
};

const shimmerStyle = {
  borderRadius: 4,
  background: 'linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
};

const labelStyle = {
  margin: 0,
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const valueStyle = {
  margin: '6px 0 0',
  fontSize: 28,
  fontWeight: 700,
  color: 'var(--color-text-default)',
  lineHeight: 1.2,
};

const deltaStyle = {
  margin: '8px 0 0',
  fontSize: 12,
  fontWeight: 500,
};

const subStyle = {
  margin: '4px 0 0',
  fontSize: 11,
  color: 'var(--color-text-muted)',
};
